import { useEffect, useRef, useState } from 'react';
import { api } from '../api';
import type { BandSearchHit } from '../types';

interface Props {
  name: string;
  bandMbid: string | null;
  imageUrl: string | null;
  onChange: (v: { name: string; bandMbid: string | null; imageUrl: string | null }) => void;
  placeholder?: string;
}

/**
 * Band name with MusicBrainz suggestions. Picking a suggestion links the band
 * (id + photo); typing anything else keeps it as plain text, so bands too small
 * for the database still work.
 */
export function BandNameInput({ name, bandMbid, imageUrl, onChange, placeholder = 'Band name' }: Props) {
  const [hits, setHits] = useState<BandSearchHit[]>([]);
  const [open, setOpen] = useState(false);
  const timer = useRef<number | null>(null);

  useEffect(() => {
    if (!open || bandMbid) { setHits([]); return; }
    if (timer.current) window.clearTimeout(timer.current);
    const q = name.trim();
    if (q.length < 2) { setHits([]); return; }
    timer.current = window.setTimeout(async () => {
      try { setHits((await api.searchBands(q)).results.slice(0, 6)); } catch { setHits([]); }
    }, 350);
    return () => { if (timer.current) window.clearTimeout(timer.current); };
  }, [name, open, bandMbid]);

  const pick = async (h: BandSearchHit) => {
    onChange({ name: h.name, bandMbid: h.mbid, imageUrl: h.imageUrl });
    setHits([]);
    setOpen(false);
    try { const full = await api.band(h.mbid); onChange({ name: full.name, bandMbid: full.mbid, imageUrl: full.imageUrl }); } catch { /* keep hit */ }
  };

  return (
    <div className="dropdown">
      <div className="row">
        {bandMbid && (imageUrl ? <img src={imageUrl} alt="" className="band-thumb" /> : <div className="band-thumb muted">♪</div>)}
        <input type="text" value={name} placeholder={placeholder} maxLength={80}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => { setOpen(false); setHits([]); }, 150)}
          onChange={(e) => onChange({ name: e.target.value, bandMbid: null, imageUrl: null })} />
      </div>
      {hits.length > 0 && (
        <div className="menu">
          {hits.map((h) => (
            <button type="button" key={h.mbid} onMouseDown={(e) => e.preventDefault()} onClick={() => pick(h)}>
              <strong>{h.name}</strong><span className="muted small"> {[h.type, h.country, h.disambiguation].filter(Boolean).join(' · ')}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
